import { StatusIndicator } from "../StatusIndicator";
import type {
  StatusIndicatorProps,
  StatusIndicatorSizes,
} from "../StatusIndicator";
import { UserImage } from "../UserImage";
import type { UserImageProps } from "../UserImage";
import type { UserImageSizes } from "../UserImage/constants";

export type UserStatus = "idle" | "offline" | "online";

/**
 * Checks if a value is of type `UserStatus`.
 */
const hasUserStatus = (status?: unknown): status is UserStatus =>
  Boolean(status) &&
  typeof status === "string" &&
  (status === "online" || status === "idle" || status === "offline");

const imageToStatusSizeMap: Record<UserImageSizes, StatusIndicatorSizes> = {
  "5": "2",
  "6": "2",
  "7": "3.5",
  "8": "3.5",
  "9": "3.5",
  "10": "3.5",
  "14": "3.5",
  "16": "5",
  "24": "5",
};

export interface UserImageStatusProps
  extends
    UserImageProps,
    Omit<StatusIndicatorProps, "hasBorder" | "size" | "status"> {
  /** User status */
  status?: UserStatus;
}

/**
 * Displays a `UserImage` with an overlaid `StatusIndicator`, if given a
 * `status` prop.
 */
export const UserImageStatus = ({
  idleColor,
  isFullRounded,
  name,
  onlineColor,
  size = "8",
  src,
  status,
}: UserImageStatusProps) => (
  <div
    className="relative inline-flex flex-col"
    data-testid="user-image-status-element"
  >
    <UserImage
      isFullRounded={isFullRounded}
      name={name}
      size={size}
      src={src}
    />
    {hasUserStatus(status) && status !== "offline" && (
      <div
        className="absolute bottom-1 right-1 translate-x-1/2 translate-y-1/2"
        data-testid="status-indicator-container"
      >
        <StatusIndicator
          idleColor={idleColor}
          onlineColor={onlineColor}
          size={imageToStatusSizeMap[size]}
          status={status}
        />
      </div>
    )}
  </div>
);

UserImageStatus.displayName = "UserImageStatus";
